import React from 'react';
import { Chart } from 'react-google-charts';
import { AggregationChartProps } from './types';
import { buildPalette, colorFor } from './color-palette';

export const AggregationChart: React.FC<AggregationChartProps> = ({
  data,
  xLabel,
  measure,
  maxBars = 10,
  orientation = 'vertical',
}) => {
  // Optional: quiet logs in production
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('AggregationChart render', { data, xLabel, measure, maxBars, orientation });
  }

  // Transform data for Google Charts
  const chartData = React.useMemo(() => {
    const sortedData = [...data]
      .map((d) => ({ ...d, yValue: Number(d.yValue) || 0 }))
      .sort((a, b) => b.yValue - a.yValue)
      .slice(0, maxBars);

    // headers: add the style role column
    const headers: any[] = [
      { type: 'string', label: xLabel },
      { type: 'number', label: measure.label },
      { type: 'string', role: 'style' }, // NEW
    ];

    // sortedData = your existing sorted & sliced top-N array
    const palette = buildPalette(Math.max(12, sortedData.length));
    const rows = sortedData.map((item) => {
      const y = Number(item.yValue) || 0; // numeric safety
      const c = colorFor(item.xValueId, sortedData.length, palette);
      return [item.xValueName, y, `color: ${c}`]; // per-row color
    });

    return [headers, ...rows];
  }, [data, xLabel, measure.label, maxBars]);

  const options = React.useMemo(() => {
    const baseOptions = {
      title: `${measure.label} ανά ${xLabel}`,
      titleTextStyle: { fontSize: 16 },
      backgroundColor: 'transparent',
      legend: { position: 'none' },
      chartArea: { left: 80, top: 60, width: '85%', height: '75%' },
      hAxis: {
        title:
          orientation === 'vertical'
            ? xLabel
            : measure.label + (measure.unit ? ` (${measure.unit})` : ''),
        titleTextStyle: { fontSize: 12 },
        textStyle: { fontSize: 11 },
      },
      vAxis: {
        title:
          orientation === 'vertical'
            ? measure.label + (measure.unit ? ` (${measure.unit})` : '')
            : xLabel,
        titleTextStyle: { fontSize: 12 },
        textStyle: { fontSize: 11 },
        format:
          measure.digits && measure.digits > 0 ? `#,##0.${'0'.repeat(measure.digits)}` : '#,##0',
      },
    } as const;

    return orientation === 'horizontal'
      ? { ...baseOptions, chartArea: { left: 120, top: 60, width: '75%', height: '75%' } }
      : baseOptions;
  }, [xLabel, measure, orientation]);

  const chartType = orientation === 'vertical' ? 'ColumnChart' : 'BarChart';

  return (
    <div className="aggregation-chart">
      <Chart
        chartType={chartType}
        width="100%"
        height="400px"
        data={chartData}
        options={options}
        loader={<div className="text-center p-4">Φόρτωση γραφήματος...</div>}
      />
    </div>
  );
};
